import React, { useEffect, useRef, useState } from 'react';
import Loader from '../loader/Loader.component';

interface ILKRedirect {
  redirecturl: string;
  additional_parameters: { [key: string]: string };
  handler?: string;
}

interface Props {
  result: ILKRedirect;
}

const ILK_KEYS = {
  acsUrl: 'consumerAuthenticationInformation.acsUrl',
  paReq: 'consumerAuthenticationInformation.paReq',
  xid: 'consumerAuthenticationInformation.xid',
  termUrl: 'consumerAuthenticationInformation.termUrl',
  threeDSSessionData: 'consumerAuthenticationInformation.threeDSSessionData',
  creq: 'consumerAuthenticationInformation.creq',
  sizeX: 'consumerAuthenticationInformation.sizeX',
  sizeY: 'consumerAuthenticationInformation.sizeY',
};

const RedirectToILKHandler: React.FC<Props> = ({ result }) => {
  const [isLoading, setIsLoading] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);

  const params = result?.additional_parameters || {};
  const acsUrl = params[ILK_KEYS.acsUrl] || result?.redirecturl || '';
  const paReq = params[ILK_KEYS.paReq] || '';
  const md = params[ILK_KEYS.xid] || '';
  const termUrl = params[ILK_KEYS.termUrl] || '';
  const threeDSSessionData = params[ILK_KEYS.threeDSSessionData] || '';
  const creq = params[ILK_KEYS.creq] || '';
  const sizeX = params[ILK_KEYS.sizeX] || '390';
  const sizeY = params[ILK_KEYS.sizeY] || '400';

  useEffect(() => {
    const form = formRef.current;
    if (!form || !acsUrl) {
      setIsLoading(false);
      return;
    }

    // Point the form at the ACS / UPOP authentication endpoint.
    form.action = acsUrl;

    // ILK forbids being framed (frame-ancestors), so the challenge must run in
    // a real top-level popup window — not an iframe.
    const popup = window.open(
      'about:blank',
      'popupWin',
      `height=${sizeY},width=${sizeX},status=no,dependent=no,scrollbars=no,resizable=no`
    );

    if (!popup) {
      // Popup blocked: fall back to a full-page top-level redirect, which is
      // also exempt from frame-ancestors.
      form.target = '_self';
      form.submit();
      return;
    }

    form.target = 'popupWin';
    form.submit();

    // Watch the popup. While it is on the ACS / acquirer domain it is
    // cross-origin and reading its location throws — that's expected, keep
    // waiting. Once the acquirer redirects it back to our own origin, mirror
    // that landing URL onto the parent window and close the popup.
    const timer = window.setInterval(() => {
      if (popup.closed) {
        window.clearInterval(timer);
        return;
      }
      let href: string | null = null;
      try {
        if (popup.location.origin === window.location.origin) {
          href = popup.location.href;
        }
      } catch (_) {
        // Still on the acquirer/ACS domain (cross-origin) — keep waiting.
        return;
      }
      if (href && !href.includes('about:blank')) {
        window.clearInterval(timer);
        popup.close();
        window.location.href = href;
      }
    }, 500);

    return () => window.clearInterval(timer);
  }, [acsUrl, paReq, md, termUrl, threeDSSessionData, creq, sizeX, sizeY]);

  return (
    <>
      {isLoading && <Loader />}
      <form
        ref={formRef}
        name="postForm"
        id="postForm"
        method="POST"
        action=""
        target="popupWin"
        style={{ display: 'none' }}
      >
        <input type="hidden" name="PaReq" id="PaReq" value={paReq} readOnly />
        <input type="hidden" name="MD" id="MD" value={md} readOnly />
        <input type="hidden" name="TermUrl" id="TermUrl" value={termUrl} readOnly />
        {/* 3DS 2.0 */}
        <input
          type="hidden"
          name="threeDSSessionData"
          id="threeDSSessionData"
          value={threeDSSessionData}
          readOnly
        />
        <input type="hidden" name="creq" id="creq" value={creq} readOnly />
      </form>
    </>
  );
};

export default RedirectToILKHandler;
